/** Reading and working the elements of the invention. */

import {
  del,
  delReturning,
  get,
  patch,
  post,
  put,
} from "@/services/api-client";
import type {
  AddElementRequest,
  AddFallbackRequest,
  AmendElementRequest,
  AmendFallbackRequest,
  InventionElementsResponse,
  InventionNamesResponse,
  NameRequest,
  PassagesResponse,
  RelationRequest,
  SubjectRequest,
} from "@patent-studio/api-types";

function elementsPath(taskId: string): string {
  return `/tasks/${taskId}/invention/elements`;
}

export function listElements(
  taskId: string,
): Promise<InventionElementsResponse> {
  return get<InventionElementsResponse>(elementsPath(taskId));
}

/** Every passage one element cites in one file, placed on the file's pages, as the drawer opens them. */
export function listPassages(
  taskId: string,
  fileId: string,
  elementId: string,
): Promise<PassagesResponse> {
  const query = new URLSearchParams({ element_id: elementId });
  return get<PassagesResponse>(
    `/tasks/${taskId}/invention/files/${fileId}/passages?${query.toString()}`,
  );
}

/**
 * Add an element the attorney supplied.
 *
 * The body carries the wording alone today. Where it came from and where it
 * sits in the sequence are fields of the same request the server already
 * defaults, so they arrive here as a wider body rather than a new endpoint.
 */
export function addElement(
  taskId: string,
  body: AddElementRequest,
): Promise<InventionElementsResponse> {
  return post<InventionElementsResponse>(elementsPath(taskId), body);
}

/** Reword one element. The wording is all an amendment ever changes. */
export function amendElement(
  taskId: string,
  elementId: string,
  body: AmendElementRequest,
): Promise<InventionElementsResponse> {
  return patch<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}`,
    body,
  );
}

/**
 * Adopt one element the tool could not ground in the disclosure.
 *
 * Its own route rather than a field on the amendment, because confirmation is a
 * deliberate act on a single element and never a side effect of an edit.
 */
export function confirmElement(
  taskId: string,
  elementId: string,
): Promise<InventionElementsResponse> {
  return post<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}/confirm`,
  );
}

/** Drop one element. Answers no content, so the caller re-reads the list. */
export function removeElement(
  taskId: string,
  elementId: string,
): Promise<void> {
  return del(`${elementsPath(taskId)}/${elementId}`);
}

/** Add a rung beneath one element, under the class it names or under the element itself. */
export function addFallback(
  taskId: string,
  elementId: string,
  body: AddFallbackRequest,
): Promise<InventionElementsResponse> {
  return post<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}/fallbacks`,
    body,
  );
}

/** Reword one rung. Its kind and its place stay as they are. */
export function amendFallback(
  taskId: string,
  elementId: string,
  fallbackId: string,
  body: AmendFallbackRequest,
): Promise<InventionElementsResponse> {
  return patch<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}/fallbacks/${fallbackId}`,
    body,
  );
}

/** Drop one rung. Answers the set, since removing a class lifts its members to the element. */
export function removeFallback(
  taskId: string,
  elementId: string,
  fallbackId: string,
): Promise<InventionElementsResponse> {
  return delReturning<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}/fallbacks/${fallbackId}`,
  );
}

/**
 * Derive the elements from the intake documents where the matter needs it: Intake's Continue.
 *
 * Safe to call again: a matter whose elements follow the files it holds, or whose elements
 * were confirmed, answers with its current state and starts nothing.
 */
export function startExtraction(
  taskId: string,
): Promise<InventionElementsResponse> {
  return post<InventionElementsResponse>(`${elementsPath(taskId)}/derive`);
}

/**
 * Read the elements again from the intake files the matter holds now: the stale-elements
 * banner's Re-run. Refused once the elements were confirmed.
 */
export function rerunExtraction(
  taskId: string,
): Promise<InventionElementsResponse> {
  return post<InventionElementsResponse>(`${elementsPath(taskId)}/rerun`);
}

/** Confirm the elements: the gate out of the page, as one stamp. */
export function settleInvention(
  taskId: string,
): Promise<InventionElementsResponse> {
  return post<InventionElementsResponse>(`/tasks/${taskId}/invention/settle`);
}

/** The term of each element and rung, where it sits in the words it names. */
export function listNames(taskId: string): Promise<InventionNamesResponse> {
  return get<InventionNamesResponse>(`/tasks/${taskId}/invention/names`);
}

/**
 * Give an element the term every claim will use for it. The names answer, not
 * the set: a term is not part of what the attorney confirms.
 */
export function nameElement(
  taskId: string,
  elementId: string,
  body: NameRequest,
): Promise<InventionNamesResponse> {
  return put<InventionNamesResponse>(
    `${elementsPath(taskId)}/${elementId}/term`,
    body,
  );
}

/** Give a rung its term, as an element's is given. */
export function nameFallback(
  taskId: string,
  elementId: string,
  fallbackId: string,
  body: NameRequest,
): Promise<InventionNamesResponse> {
  return put<InventionNamesResponse>(
    `${elementsPath(taskId)}/${elementId}/fallbacks/${fallbackId}/term`,
    body,
  );
}

/** Write the thing claimed. The confirmation covers it, so the whole set answers. */
export function setSubject(
  taskId: string,
  body: SubjectRequest,
): Promise<InventionElementsResponse> {
  return put<InventionElementsResponse>(
    `/tasks/${taskId}/invention/subject`,
    body,
  );
}

/**
 * Say which element this one attaches to, and the words of its text that say
 * how. The confirmation covers it, so the whole set answers.
 */
export function relateElement(
  taskId: string,
  elementId: string,
  body: RelationRequest,
): Promise<InventionElementsResponse> {
  return put<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}/relation`,
    body,
  );
}

/** Say the element attaches to nothing. */
export function unrelateElement(
  taskId: string,
  elementId: string,
): Promise<InventionElementsResponse> {
  return delReturning<InventionElementsResponse>(
    `${elementsPath(taskId)}/${elementId}/relation`,
  );
}
